import { v4 as uuidv4 } from "uuid"
import { hashString } from "../crypto/hash"

/**
 * Enum for preset item types that need deterministic IDs
 */
export enum PresetItemType {
  USER_SETTINGS = "user_settings",
  GLOBAL_TAGS = "global_tags",
}

/**
 * Generate a deterministic UUID for preset items based on user email and item type
 * @param itemType The type of preset item
 * @param userEmail The user's email address
 * @returns A deterministic UUID string
 */
export const generatePresetId = async (
  itemType: PresetItemType,
  userEmail: string,
): Promise<string> => {
  // Normalize inputs to lowercase
  const normalizedEmail = userEmail.toLowerCase().trim()
  const normalizedItemType = itemType.toLowerCase()

  // Create a combined string to hash
  const combinedString = `${normalizedEmail}:${normalizedItemType}`

  // Hash the combined string
  const hashHex = await hashString(combinedString)

  // Convert the first 32 hex characters (16 bytes) to a byte array
  const randomBytes = new Uint8Array(16)
  for (let i = 0; i < 16; i++) {
    const hexByte = hashHex.substring(i * 2, i * 2 + 2)
    randomBytes[i] = parseInt(hexByte, 16)
  }

  // Generate UUID v4 using the deterministic bytes
  return uuidv4({ random: randomBytes })
}

/**
 * Generates a unique identifier (UUID v4) for items
 * This function is intended to be used across the codebase
 * for consistent ID generation for items, revisions, etc.
 * It ensures the generated ID doesn't conflict with any preset IDs.
 *
 * @returns {string} A unique UUID v4 string
 */
export const generateId = (): string => {
  // For regular items, just use standard UUID v4
  // Preset IDs are deterministic and won't conflict with random UUIDs
  return uuidv4()
}
